const _jsxFileName = '';
function _optionalChain(ops) {
  let lastAccessLHS = undefined;
  let value = ops[0];
  let i = 1;
  while (i < ops.length) {
    const op = ops[i];
    const fn = ops[i + 1];
    i += 2;
    if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) {
      return undefined;
    }
    if (op === 'access' || op === 'optionalAccess') {
      lastAccessLHS = value;
      value = fn(value);
    } else if (op === 'call' || op === 'optionalCall') {
      value = fn((...args) => value.call(lastAccessLHS, ...args));
      lastAccessLHS = undefined;
    }
  }
  return value;
}
import React, { useState } from 'react';
import { MapPin, Phone, ChevronDown, Send, ArrowRight, Star } from 'lucide-react';

const faqData = [
  {
    question: 'Do you offer private viewings?',
    answer:
      "Yes. We offer private appointments in our Manila atelier or virtual consultations with our senior gemologists. Please select 'Private Appointment' in the contact form.",
  },
  {
    question: 'How are your gemstones authenticated?',
    answer:
      'Every piece comes with a GIA or AGS certificate. Our in-house experts verify provenance and quality before any item enters our curated collection.',
  },
  {
    question: 'What is your shipping policy?',
    answer:
      'We provide fully insured, armored courier shipping within the Philippines. International shipping is handled via secure logistics partners with customs handling included.',
  },
];

// API configuration
const API_BASE_URL = import.meta.env.VITE_API_URL;

const Contact = ({ settings }) => {
  const [activeAccordion, setActiveAccordion] = useState(0);
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    subject: 'General Inquiry',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState(null);

  // Use settings or fallbacks
  const email =
    _optionalChain([settings, 'optionalAccess', (_) => _.email]) || 'rsgleamofgolds@gmail.com';
  const phone = _optionalChain([settings, 'optionalAccess', (_2) => _2.phone]) || '0945 392 8946';
  const address =
    _optionalChain([settings, 'optionalAccess', (_3) => _3.address]) ||
    '2F, San Antonio Building, Corumi, Quezon City, 1012 Metro Manila';
  const hours =
    _optionalChain([settings, 'optionalAccess', (_4) => _4.hours]) || 'Mon-Sun: 9am - 8pm';

  const toggleAccordion = (index) => {
    setActiveAccordion(activeAccordion === index ? null : index);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Send to your backend API
      const response = await fetch(`${API_BASE_URL}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formState,
          recipient: email, // Use the email from settings
        }),
      });

      const data = await response.json();

      if (data.success) {
        setIsSent(true);
        setFormState({ name: '', email: '', subject: 'General Inquiry', message: '' });
      } else {
        setError(data.error || 'Failed to send message. Please try again.');
      }
    } catch (err) {
      setError('Connection error. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return React.createElement(
    'section',
    {
      id: 'contact',
      className: 'relative bg-stone-50 pt-48 pb-0',
      __self: this,
      __source: { fileName: _jsxFileName, lineNumber: 86 },
    },
    /* Texture Overlay */
    React.createElement('div', {
      className: 'absolute inset-0 bg-noise opacity-40 pointer-events-none',
      __self: this,
      __source: { fileName: _jsxFileName, lineNumber: 88 },
    }),

    /* Header Section */
    React.createElement(
      'div',
      {
        className: 'max-w-7xl mx-auto px-6 mb-20 relative z-10 text-center',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 91 },
      },
      React.createElement(
        'span',
        {
          className:
            'text-gold-600 text-xs tracking-[0.3em] uppercase font-bold mb-4 block animate-fade-in',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 92 },
        },
        'Concierge Services'
      ),
      React.createElement(
        'h2',
        {
          className: 'font-serif text-5xl md:text-6xl text-stone-900 mb-6',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 95 },
        },
        'Bespoke ',
        React.createElement(
          'span',
          {
            className: 'italic text-gold-600',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 96 },
          },
          'Inquiries'
        )
      ),
      React.createElement(
        'p',
        {
          className: 'text-stone-600 font-light max-w-2xl mx-auto leading-relaxed',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 98 },
        },
        'Whether you seek a rare gemstone, require a private viewing, or have questions about an heirloom, our dedicated team is at your disposal.'
      )
    ),

    React.createElement(
      'div',
      {
        className: 'max-w-7xl mx-auto px-6 relative z-10',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 103 },
      },
      React.createElement(
        'div',
        {
          className: 'grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 104 },
        },

        /* LEFT COLUMN: Contact Form */
        React.createElement(
          'div',
          {
            className:
              'bg-white p-8 md:p-12 shadow-2xl shadow-stone-200/50 border border-stone-100 relative overflow-hidden',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 107 },
          },
          /* Decorative decorative element */
          React.createElement('div', {
            className:
              'absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-gold-300 via-gold-500 to-gold-300',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 109 },
          }),

          error &&
            React.createElement(
              'div',
              {
                className:
                  'mb-6 p-3 bg-red-50 border border-red-200 text-red-600 text-sm rounded-sm',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 112 },
              },
              error
            ),

          !isSent
            ? React.createElement(
                'form',
                {
                  onSubmit: handleSubmit,
                  className: 'space-y-8 relative z-10',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 118 },
                },
                React.createElement(
                  'div',
                  {
                    className: 'space-y-6',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 119 },
                  },
                  React.createElement(
                    'div',
                    {
                      className: 'group relative',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 120 },
                    },
                    React.createElement('input', {
                      type: 'text',
                      required: true,
                      value: formState.name,
                      onChange: (e) => setFormState({ ...formState, name: e.target.value }),
                      className:
                        'w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-gold-600 transition-colors peer placeholder-transparent',
                      placeholder: 'Your Name',
                      id: 'name',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 121 },
                    }),
                    React.createElement(
                      'label',
                      {
                        htmlFor: 'name',
                        className:
                          'absolute left-0 -top-3.5 text-xs text-gold-600 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-stone-400 peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-gold-600 peer-focus:text-xs uppercase tracking-wider',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 130 },
                      },
                      'Your Name'
                    )
                  ),

                  React.createElement(
                    'div',
                    {
                      className: 'group relative',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 138 },
                    },
                    React.createElement('input', {
                      type: 'email',
                      required: true,
                      value: formState.email,
                      onChange: (e) => setFormState({ ...formState, email: e.target.value }),
                      className:
                        'w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-gold-600 transition-colors peer placeholder-transparent',
                      placeholder: 'Email Address',
                      id: 'email',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 139 },
                    }),
                    React.createElement(
                      'label',
                      {
                        htmlFor: 'email',
                        className:
                          'absolute left-0 -top-3.5 text-xs text-gold-600 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-stone-400 peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-gold-600 peer-focus:text-xs uppercase tracking-wider',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 148 },
                      },
                      'Email Address'
                    )
                  ),

                  React.createElement(
                    'div',
                    {
                      className: 'group relative',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 156 },
                    },
                    React.createElement(
                      'select',
                      {
                        value: formState.subject,
                        onChange: (e) => setFormState({ ...formState, subject: e.target.value }),
                        className:
                          'w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-gold-600 transition-colors appearance-none cursor-pointer',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 157 },
                      },

                      React.createElement(
                        'option',
                        { __self: this, __source: { fileName: _jsxFileName, lineNumber: 162 } },
                        'General Inquiry'
                      ),
                      React.createElement(
                        'option',
                        { __self: this, __source: { fileName: _jsxFileName, lineNumber: 163 } },
                        'Private Appointment'
                      ),
                      React.createElement(
                        'option',
                        { __self: this, __source: { fileName: _jsxFileName, lineNumber: 164 } },
                        'Product Authentication'
                      ),
                      React.createElement(
                        'option',
                        { __self: this, __source: { fileName: _jsxFileName, lineNumber: 165 } },
                        'Press & Media'
                      )
                    ),
                    React.createElement(
                      'label',
                      {
                        className:
                          'absolute left-0 -top-3.5 text-xs text-gold-600 uppercase tracking-wider',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 167 },
                      },
                      'Subject'
                    ),
                    React.createElement(ChevronDown, {
                      className: 'absolute right-0 top-3 text-stone-400 pointer-events-none',
                      size: 16,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 170 },
                    })
                  ),

                  React.createElement(
                    'div',
                    {
                      className: 'group relative',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 173 },
                    },
                    React.createElement('textarea', {
                      rows: 4,
                      required: true,
                      value: formState.message,
                      onChange: (e) => setFormState({ ...formState, message: e.target.value }),
                      className:
                        'w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-gold-600 transition-colors peer placeholder-transparent resize-none',
                      placeholder: 'Message',
                      id: 'message',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 174 },
                    }),
                    React.createElement(
                      'label',
                      {
                        htmlFor: 'message',
                        className:
                          'absolute left-0 -top-3.5 text-xs text-gold-600 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-stone-400 peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-gold-600 peer-focus:text-xs uppercase tracking-wider',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 183 },
                      },
                      'How may we assist you?'
                    )
                  )
                ),

                React.createElement(
                  'button',
                  {
                    type: 'submit',
                    disabled: isSubmitting,
                    className:
                      'w-full bg-stone-900 text-white py-4 px-8 uppercase tracking-[0.2em] text-xs hover:bg-gold-600 transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed group',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 192 },
                  },

                  isSubmitting ? 'Sending...' : 'Send Message',
                  !isSubmitting &&
                    React.createElement(ArrowRight, {
                      size: 14,
                      className: 'group-hover:translate-x-1 transition-transform',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 198 },
                    })
                )
              )
            : React.createElement(
                'div',
                {
                  className:
                    'h-full flex flex-col items-center justify-center text-center py-20 animate-fade-in',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 202 },
                },
                React.createElement(
                  'div',
                  {
                    className:
                      'w-16 h-16 bg-gold-100 rounded-full flex items-center justify-center mb-6 text-gold-600',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 203 },
                  },
                  React.createElement(Send, {
                    size: 24,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 204 },
                  })
                ),
                React.createElement(
                  'h3',
                  {
                    className: 'font-serif text-2xl mb-2',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 206 },
                  },
                  'Message Received'
                ),
                React.createElement(
                  'p',
                  {
                    className: 'text-stone-500 font-light text-sm max-w-xs',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 207 },
                  },
                  'Thank you for contacting Sparkling Gems. A concierge will respond to your inquiry within 24 hours.'
                ),
                React.createElement(
                  'button',
                  {
                    onClick: () => setIsSent(false),
                    className:
                      'mt-8 text-xs uppercase tracking-widest text-stone-900 border-b border-stone-900 pb-1 hover:text-gold-600 hover:border-gold-600 transition-colors',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 210 },
                  },
                  'Send another message'
                )
              )
        ),

        /* RIGHT COLUMN: Info & FAQ */
        React.createElement(
          'div',
          {
            className: 'flex flex-col justify-between',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 221 },
          },

          /* Contact Cards */
          React.createElement(
            'div',
            {
              className: 'grid grid-cols-1 md:grid-cols-2 gap-6 mb-12',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 224 },
            },
            React.createElement(
              'div',
              {
                className:
                  'bg-white p-6 border border-stone-100 shadow-sm hover:shadow-md transition-shadow group cursor-default',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 225 },
              },
              React.createElement(Phone, {
                className: 'text-gold-500 mb-4 group-hover:scale-110 transition-transform',
                size: 24,
                strokeWidth: 1.5,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 226 },
              }),
              React.createElement(
                'h4',
                {
                  className: 'font-serif text-lg mb-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 227 },
                },
                'Concierge'
              ),
              React.createElement(
                'div',
                {
                  className: 'text-xs text-stone-500 leading-relaxed',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 228 },
                },
                React.createElement(
                  'p',
                  { __self: this, __source: { fileName: _jsxFileName, lineNumber: 229 } },
                  phone
                ),
                React.createElement(
                  'p',
                  {
                    className: 'break-all',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 230 },
                  },
                  email
                ),
                React.createElement(
                  'p',
                  {
                    className: 'mt-2',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 231 },
                  },
                  hours
                )
              )
            ),
            React.createElement(
              'div',
              {
                className:
                  'bg-white p-6 border border-stone-100 shadow-sm hover:shadow-md transition-shadow group cursor-default',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 234 },
              },
              React.createElement(MapPin, {
                className: 'text-gold-500 mb-4 group-hover:scale-110 transition-transform',
                size: 24,
                strokeWidth: 1.5,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 235 },
              }),
              React.createElement(
                'h4',
                {
                  className: 'font-serif text-lg mb-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 236 },
                },
                'The Atelier'
              ),
              React.createElement(
                'div',
                {
                  className: 'text-xs text-stone-500 leading-relaxed',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 237 },
                },
                React.createElement(
                  'p',
                  {
                    className: 'whitespace-pre-wrap',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 238 },
                  },
                  address
                ),
                React.createElement(
                  'p',
                  {
                    className: 'mt-2',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 239 },
                  },
                  'By Appointment Only'
                )
              )
            )
          ),

          /* FAQ Accordion */
          React.createElement(
            'div',
            {
              className: 'bg-white/60 backdrop-blur-sm border border-white p-8 rounded-sm',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 245 },
            },
            React.createElement(
              'h3',
              {
                className: 'text-sm font-bold uppercase tracking-widest text-stone-900 mb-6',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 246 },
              },
              'Client Services'
            ),
            React.createElement(
              'div',
              {
                className: 'divide-y divide-stone-200',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 247 },
              },
              faqData.map((item, index) =>
                React.createElement(
                  'div',
                  {
                    key: index,
                    className: 'py-4',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 249 },
                  },
                  React.createElement(
                    'button',
                    {
                      onClick: () => toggleAccordion(index),
                      className: 'w-full flex justify-between items-center text-left group',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 250 },
                    },

                    React.createElement(
                      'span',
                      {
                        className: `font-serif text-lg transition-colors ${activeAccordion === index ? 'text-gold-600' : 'text-stone-800 group-hover:text-gold-600'}`,
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 254 },
                      },
                      item.question
                    ),
                    React.createElement(ChevronDown, {
                      size: 16,
                      className: `text-stone-400 transition-transform duration-300 ${activeAccordion === index ? 'rotate-180 text-gold-600' : ''}`,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 257 },
                    })
                  ),
                  React.createElement(
                    'div',
                    {
                      className: `overflow-hidden transition-all duration-500 ease-in-out ${
                        activeAccordion === index
                          ? 'max-h-32 opacity-100 mt-3'
                          : 'max-h-0 opacity-0'
                      }`,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 262 },
                    },

                    React.createElement(
                      'p',
                      {
                        className: 'text-stone-500 text-xs leading-relaxed font-light pr-8',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 267 },
                      },
                      item.answer
                    )
                  )
                )
              )
            )
          ),

          /* Trust Badge */
          React.createElement(
            'div',
            {
              className: 'mt-8 flex items-center gap-4 opacity-70',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 277 },
            },
            React.createElement(
              'div',
              {
                className: 'flex text-gold-500',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 278 },
              },
              [1, 2, 3, 4, 5].map((i) =>
                React.createElement(Star, {
                  key: i,
                  size: 14,
                  fill: 'currentColor',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 279 },
                })
              )
            ),
            React.createElement(
              'span',
              {
                className: 'text-[10px] uppercase tracking-widest text-stone-500',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 281 },
              },
              'Rated 5.0 for Service Excellence'
            )
          )
        )
      )
    ),

    /* Styled Map Section */
    React.createElement(
      'div',
      {
        id: 'location-map',
        className: 'relative w-full h-96 mt-24 group overflow-hidden',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 290 },
      },
      /* Grayscale filter overlay */
      React.createElement('div', {
        className: 'absolute inset-0 bg-stone-900/10 z-10 pointer-events-none mix-blend-saturation',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 292 },
      }),
      React.createElement(
        'div',
        {
          className:
            'absolute inset-0 z-20 pointer-events-none flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-stone-900/20 backdrop-blur-[1px]',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 293 },
        },
        React.createElement(
          'a',
          {
            href: 'https://www.google.com/maps/place/Sparkling+Gems+Corp/@14.6399511,121.009818,1091m/data=!3m2!1e3!4b1!4m6!3m5!1s0x3397b7541c9ed635:0x1e8f258cec07a7a1!8m2!3d14.6399511!4d121.009818!16s%2Fg%2F11yz7q5q2r?entry=ttu&g_ep=EgoyMDI2MDIxMS4wIKXMDSoASAFQAw%3D%3D',
            target: '_blank',
            rel: 'noopener noreferrer',
            className:
              'pointer-events-auto bg-white text-stone-900 px-6 py-3 uppercase text-xs tracking-widest font-bold shadow-xl transform translate-y-4 group-hover:translate-y-0 transition-all hover:bg-gold-600 hover:text-white',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 294 },
          },
          'Open in Google Maps'
        )
      ),

      React.createElement('iframe', {
        src: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3860.203874312389!2d121.009818!3d14.6399511!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3397b7541c9ed635%3A0x1e8f258cec07a7a1!2sSparkling%20Gems%20Corp!5e0!3m2!1sen!2sph!4v1709228955821!5m2!1sen!2sph',
        width: '100%',
        height: '100%',
        style: { border: 0, filter: 'grayscale(100%) contrast(1.2)' },
        allowFullScreen: false,
        loading: 'lazy',
        referrerPolicy: 'no-referrer-when-downgrade',
        className: 'w-full h-full',
        title: 'Sparkling Gems Location',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 304 },
      })
    )
  );
};

export default Contact;
